import React, { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { ColorPickerButton } from '@crystaldelta/loree-ui-color-picker';
import { CreateNewTableProps, borderCollapseOption, borderStyles } from '../lintEditorType';
import { getCapitalizeWord, numberValidation, rgbToHex } from '../utilityFunctions';
import {
  BorderTableMenuButton,
  BorderTableMenuDropdown,
  BorderTableMenuOptionButton,
  BorderTableMenuSection,
} from '../styledContents';
import { ReactComponent as LeftAlignmentButton } from '../../editorFormattingIcons/leftAlignmentButton.svg';
import { ReactComponent as CenterAlignmentButton } from '../../editorFormattingIcons/centerAlignmentButton.svg';
import { ReactComponent as RightAlignmentButton } from '../../editorFormattingIcons/rightAlignmentButton.svg';
import { ReactComponent as TableDropDownIcon } from '../../editorFormattingIcons/dropDownMenu.svg';
import { CONSTANTS } from '../constants';

export const TableProperties = (props: CreateNewTableProps) => {
  const { onHide, onSubmitTableProperties, styles } = props;
  const borderStyleProps = styles?.borderStyle ? getCapitalizeWord(styles?.borderStyle) : 'solid';
  const borderCollapseProps = styles?.borderStyle
    ? getCapitalizeWord(styles?.borderCollapse)
    : 'solid';

  let tableAlignmentValue = 'left';
  if (styles?.alignment) {
    switch (styles?.alignment) {
      case '0px auto':
        tableAlignmentValue = 'center';
        break;
      case 'auto 0px auto auto':
        tableAlignmentValue = 'right';
        break;
    }
  }

  const [widthvalue, setWidthvalue] = useState(styles?.width ?? '100');
  const [alignmentValue, setAlignmentValue] = useState(tableAlignmentValue ?? 'left');
  const [borderWidth, setBorderwidth] = useState(styles?.border ?? '3');
  const [borderStyle, setBorderStyle] = useState(borderStyleProps);
  const [borderCollapse, setBorderCollapse] = useState(borderCollapseProps);
  const [borderColor, setBorderColor] = useState(
    styles?.borderColor && styles?.borderColor === 'rgb(0, 0, 0)'
      ? CONSTANTS.BORDER_COLOR
      : styles?.borderColor ?? CONSTANTS.BORDER_COLOR,
  );
  const [backgroundColor, setBackgroundColor] = useState(
    styles?.backgroundColor && styles?.backgroundColor === 'rgb(255, 255, 255)'
      ? CONSTANTS.BG_COLOR
      : styles?.backgroundColor ?? CONSTANTS.BG_COLOR,
  );

  const widthSetup = (target: EventTarget) => {
    const value = (target as HTMLInputElement).value;
    if (parseInt(value) > 100) {
      setWidthvalue('100');
    } else if (parseInt(value) < 1) {
      setWidthvalue('1');
    } else {
      setWidthvalue(value);
    }
  };

  const borderWidthSetup = (target: EventTarget) => {
    const value = (target as HTMLInputElement).value;
    if (parseInt(value) < 0) {
      setBorderwidth('0');
    } else {
      setBorderwidth(value);
    }
  };

  const applyStyles = () => {
    const widthValueStyle = widthvalue ? widthvalue + '%' : '100%';
    let alignmentValueStyle = 'auto auto auto 0px';
    switch (alignmentValue) {
      case 'center':
        alignmentValueStyle = '0px auto';
        break;
      case 'right':
        alignmentValueStyle = 'auto 0px auto auto';
        break;
    }

    const borderFullStyle =
      (borderWidth ? borderWidth : '0') +
      'px ' +
      (borderStyle ? borderStyle : 'solid') +
      ' ' +
      (borderColor ? borderColor : '#000000');

    const borderCollapseStyle = borderCollapse ? borderCollapse : 'collapse';
    const backgroundColorStyle = backgroundColor ? backgroundColor : 'transparent';

    onSubmitTableProperties &&
      onSubmitTableProperties(
        widthValueStyle,
        alignmentValueStyle,
        borderFullStyle,
        borderCollapseStyle,
        backgroundColorStyle,
      );
    onHide();
  };

  return (
    <>
      <Modal.Body className='pb-0 pt-3'>
        <div className='row'>
          <div className='col-lg-6 col-md-6 col-sm-6 sizeSection mb-2'>
            <Form.Label className='tablePropertyLabelSection'>Width</Form.Label>
            <div className='d-flex mt-0'>
              <Form.Control
                type='number'
                inputMode='numeric'
                className='loree-table-text-element lintTableTextBox borderWidthContent'
                min='0'
                max='100'
                tabIndex={0}
                defaultValue={styles?.width}
                value={widthvalue}
                onKeyDown={numberValidation}
                onChange={(e) => widthSetup(e.target)}
              />
              <b className=' d-flex matricDiv'>%</b>
            </div>
          </div>
          <div className='col-lg-6 col-md-6 col-sm-6 mb-2'>
            <Form.Label className='tablePropertyLabelSection'>Alignment</Form.Label>
            <div className='d-flex tableAlignmentpropertySection col-lg-10 col-md-10 col-sm-12'>
              <LeftAlignmentButton
                tabIndex={0}
                className={
                  alignmentValue === 'left'
                    ? 'alignmentButtonTableProperties active'
                    : 'alignmentButtonTableProperties'
                }
                onClick={() => setAlignmentValue('left')}
                onKeyDown={() => setAlignmentValue('left')}
              />
              <CenterAlignmentButton
                tabIndex={0}
                className={
                  alignmentValue === 'center'
                    ? 'alignmentButtonTableProperties active'
                    : 'alignmentButtonTableProperties'
                }
                onClick={() => setAlignmentValue('center')}
                onKeyDown={() => setAlignmentValue('center')}
              />
              <RightAlignmentButton
                tabIndex={0}
                className={
                  alignmentValue === 'right'
                    ? 'alignmentButtonTableProperties active'
                    : 'alignmentButtonTableProperties'
                }
                onClick={() => setAlignmentValue('right')}
                onKeyDown={() => setAlignmentValue('right')}
              />
            </div>
          </div>
          <div className='d-flex mb-2'>
            <div className='col-lg-6 col-md-6 col-sm-6 col-6 mt-0'>
              <Form.Label className='tablePropertyLabelSection '>Border Color</Form.Label>
              <div className='d-flex'>
                <input
                  type='string'
                  className='colorInputbox'
                  value={borderColor}
                  onChange={(e) => {
                    const newColor = e.target.value.trim();
                    const CurrentColor = rgbToHex(newColor, CONSTANTS.BORDER_COLOR);
                    setBorderColor(CurrentColor);
                  }}
                  readOnly
                />
                <ColorPickerButton
                  className='colorPicker'
                  label='Background color'
                  currentColor={borderColor}
                  onChange={(color) => {
                    setBorderColor(color || CONSTANTS.BORDER_COLOR);
                  }}
                  globalColorList={[]}
                  sharedColors={[]}
                  colorPickerFeatures={CONSTANTS.LOREE_COLORPICKER_FEATURES}
                  colorPickerType='Background'
                  colorContrastRatio={() => {}}
                />
              </div>
            </div>
            <div className='col-lg-4 col-md-4 col-sm-4 col-4 mt-0 borderWidthSection '>
              <Form.Label className='tablePropertyLabelSection'>Width</Form.Label>
              <div className='d-flex'>
                <Form.Control
                  type='number'
                  inputMode='numeric'
                  className='loree-table-text-element lintTableTextBox borderWidthContent'
                  min='0'
                  tabIndex={0}
                  value={borderWidth}
                  onKeyDown={numberValidation}
                  onChange={(e) => {
                    borderWidthSetup(e.target);
                  }}
                />
                <b className='tablePropertyLabelSection ' style={{ marginLeft: '10px' }}>
                  px
                </b>
              </div>
            </div>
          </div>
          <div className='col-lg-12 col-md-12 col-sm-12 mt-1 mb-2'>
            <Form.Group className=' w-100 position-relative'>
              <Form.Label className='tablePropertyLabelSection tableBorderStyleLabel w-auto'>
                Border Style
              </Form.Label>
              <BorderTableMenuSection className='w-100 borderInput'>
                <BorderTableMenuDropdown
                  width='210px'
                  placement='bottom'
                  element={
                    <BorderTableMenuButton className='w-100'>
                      {getCapitalizeWord(borderStyle)}
                    </BorderTableMenuButton>
                  }
                >
                  {borderStyles.map((optionValue) => {
                    return (
                      <BorderTableMenuOptionButton
                        key={optionValue}
                        data-testid='add-table'
                        name='LintTables'
                        className={optionValue === getCapitalizeWord(borderStyle) ? 'active' : ''}
                        onClick={() => setBorderStyle(optionValue.toLowerCase())}
                      >
                        {optionValue}
                      </BorderTableMenuOptionButton>
                    );
                  })}
                </BorderTableMenuDropdown>
              </BorderTableMenuSection>
              <TableDropDownIcon className='borderSearchIcon' />
            </Form.Group>
          </div>
          <div className='d-flex'>
            <div className='col-lg-6 col-md-6 col-sm-6 mt-2 mb-2'>
              <Form.Label className='tablePropertyLabelSection tableBorderStyleLabel w-auto'>
                Border Collapse
              </Form.Label>
              <Form.Group className='d-flex position-relative borderCollapseDiv '>
                <BorderTableMenuSection className='w-100 borderCollapse'>
                  <BorderTableMenuDropdown
                    width='301px'
                    placement='bottom'
                    element={
                      <BorderTableMenuButton className='w-100'>
                        {getCapitalizeWord(borderCollapse)}
                      </BorderTableMenuButton>
                    }
                  >
                    {borderCollapseOption.map((optionValue) => {
                      return (
                        <BorderTableMenuOptionButton
                          key={optionValue}
                          data-testid='add-table'
                          name='LintTables'
                          className={
                            optionValue === getCapitalizeWord(borderCollapse) ? 'active' : ''
                          }
                          onClick={() => setBorderCollapse(optionValue.toLowerCase())}
                        >
                          {optionValue}
                        </BorderTableMenuOptionButton>
                      );
                    })}
                  </BorderTableMenuDropdown>
                </BorderTableMenuSection>
                <TableDropDownIcon className='borderSearchIcon' />
              </Form.Group>
            </div>
            <div className='mt-1'>
              <div className='col-lg-6 col-md-6 col-sm-6 col-6 d-flex mt-0'>
                <Form.Label className='tablePropertyLabelSection mb-0'>Background Color</Form.Label>
              </div>
              <div className='col-lg-6 col-md-6 col-sm-6 d-flex col-6 mt-0'>
                <input
                  type='text'
                  className='colorInputbox mt-2'
                  value={backgroundColor}
                  onChange={(e) => {
                    const newColor = e.target.value.trim();
                    const CurrentColor = rgbToHex(newColor, CONSTANTS.BG_COLOR);
                    setBackgroundColor(CurrentColor);
                  }}
                  readOnly
                />
                <ColorPickerButton
                  className='colorPicker mt-2'
                  label='Background color'
                  currentColor={backgroundColor}
                  onChange={(color) => {
                    setBackgroundColor(color || CONSTANTS.BG_COLOR);
                  }}
                  globalColorList={[]}
                  sharedColors={[]}
                  colorPickerFeatures={CONSTANTS.LOREE_COLORPICKER_FEATURES}
                  colorPickerType='Background'
                  colorContrastRatio={() => { }}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className='d-flex CellFooter'>
        <Button className='modalCancelButton' onClick={() => onHide()}>
          Cancel
        </Button>
        <Button className='modalOkButton' onClick={() => applyStyles()}>
          Apply
        </Button>
      </Modal.Footer>
    </>
  );
};
